import { useState } from "react";
import TokenSummary from "../../../components/common/ReportBox";
import { Container } from "@mui/material";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Grid from "@mui/material/Grid2";
import { GetAllSummaryReport } from "../../../lib/api";
import { useQuery } from "@tanstack/react-query";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

const Summary = () => {
  const [date, setDate] = useState(new Date());
  const formattedDate = date.toLocaleDateString("en-CA");
  const [isDownloadExcel, setIsDownloadExcel] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["GetAllSummaryReport", formattedDate],
    queryFn: () => GetAllSummaryReport(formattedDate),
  });
  const convertMinutesToHours = (minutes) => {
    if (!minutes || minutes <= 0) return "0 minutes";

    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hrs > 0) {
      return `${hrs} ${hrs === 1 ? "hour" : "hours"} ${
        mins > 0 ? `${mins} ${mins === 1 ? "minute" : "minutes"}` : ""
      }`;
    }
    return `${mins} ${mins === 1 ? "minute" : "minutes"}`;
  };

  const renderContent = (value, placeholder = "Loading...") => {
    if (isLoading) return placeholder;
    if (isError || value === undefined) return "N/A";
    return value;
  };

  const handleDownloadExcel = async () => {
    if (isLoading) return;
    if (!data) {
      toast.error("No data to Download", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setIsDownloadExcel(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Token Summary Report");
      worksheet.addRow([]);
      const headerRow = worksheet.addRow(["Particulars", ""]);
      headerRow.font = { bold: true };
      headerRow.getCell(1).alignment = { horizontal: "left" };
      headerRow.getCell(2).alignment = { horizontal: "right" };

      const particulars = [
        {
          label: "Token Issued",
          value: `${data?.data?.summaryReport?.totalTokensIssued || 0}`,
        },
        {
          label: "Tokens Served",
          value: `${data?.data?.totalCompletedTokens || 0}`,
        },
        {
          label: "Tokens Transferred",
          value: `${data?.data?.totalTransferredTokens || 0}`,
        },
        {
          label: "Average Wait Time",
          value: `${
            convertMinutesToHours(
              data?.data?.summaryReport?.averageWaitTimeMinutes
            ) || 0
          }`,
        },
        {
          label: "Peak Hours",
          value: `Time: ${data.data.summaryReport.peakHour.hour}\nToken Issued: ${data.data.summaryReport.peakHour.tokensIssued}`,
        },
        {
          label: "Performance",
          value:
            data?.data?.summaryReport?.counterPerformanceData
              ?.map(
                (item) =>
                  `Counter : ${item.counterName}\r\nServed : ${item.tokensServed}`
              )
              .join("\r\n\r\n") || "No data",
        },
        {
          label: "Unserved Tokens",
          value: `OnHold : ${
            data?.data?.summaryReport?.tokensOnHold || 0
          }\r\nPending : ${data?.data?.summaryReport?.tokensPending || 0}`,
        },
      ];

      particulars.forEach((item) => {
        const row = worksheet.addRow([item.label, item.value]);
        row.getCell(2).alignment = { wrapText: true };
      });

      worksheet.addRow([]);

      const generatedDate = new Date().toLocaleString();
      worksheet.addRow(["Generated Date:", generatedDate]);

      worksheet.columns = [
        { key: "particulars", width: 25 },
        { key: "amount", width: 20 },
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Payment Summary Report.xlsx");
    } catch (error) {
      toast.error("No data to Download", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setIsDownloadExcel(false);
  };

  return (
    <>
      <div className="mt-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-xl min-[480px]:text-2xl sm:text-3xl font-bold">
            Token Summary Report
          </h4>
          <div className="relative">
            <img
              className="cursor-pointer flex  w-[60px]"
              onClick={handleDownloadExcel}
              src="/image/element/excel.png"
              alt="excel_icon"
            />
            {isDownloadExcel && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded z-10">
                <div className="flex justify-center items-center m-auto">
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div id="date-range-picker" className="flex items-center w-full ml-7">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none ">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
              </svg>
            </div>
            <Flatpickr
              value={date}
              onChange={(selectedDate) => setDate(selectedDate[0])}
              options={{ dateFormat: "Y-m-d" }}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Select a date"
            />
          </div>
        </div>
      </div>

      <Container sx={{ my: 4 }}>
        <Grid container spacing={4}>
          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              header={
                " Total: " +
                renderContent(data?.data?.summaryReport?.totalTokensIssued || 0)
              }
              Tittle={" Token Issued"}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              Served={"Tokens Served"}
              header={
                " Total : " +
                renderContent(data?.data?.totalCompletedTokens || 0)
              }
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              Transferred={"Tokens Transferred"}
              header={
                "Total : " +
                renderContent(data?.data?.totalTransferredTokens || 0)
              }
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              Average={"Average Wait Time"}
              header={
                "Time: " +
                renderContent(
                  convertMinutesToHours(
                    data?.data?.summaryReport?.averageWaitTimeMinutes
                  ),
                  "Loading..."
                )
              }
            />
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              Peak={"Peak Hours"}
              endtime={renderContent(
                data?.data?.summaryReport?.peakHour,
                "Loading..."
              )}
            />
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              Counter={" Performance"}
              peaktime={data?.data?.summaryReport?.counterPerformanceData}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <TokenSummary
              Unserved={"Unserved Tokens"}
              header={
                "OnHold : " +
                renderContent(data?.data?.summaryReport?.tokensOnHold || 0)
              }
              headerNew={
                "Pending : " +
                renderContent(data?.data?.summaryReport?.tokensPending || 0)
              }
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Summary;
